import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Stack,
  Avatar,
  LinearProgress,
  IconButton
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';

interface BugReport {
  title: string;
  summary: string;
  browserInfo: string;
  userEmail?: string;
}

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (bugReport: BugReport) => Promise<void>;
  userEmail?: string;
}

const BugReportModal: React.FC<BugReportModalProps> = ({
  open,
  onClose,
  onSubmit,
  userEmail
}) => {
  const [formData, setFormData] = useState<BugReport>({
    title: '',
    summary: '',
    browserInfo: getBrowserInfo(),
    userEmail: userEmail || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function getBrowserInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const cookieEnabled = navigator.cookieEnabled;
    const onlineStatus = navigator.onLine ? 'Online' : 'Offline';
    
    return `User Agent: ${userAgent}\nPlatform: ${platform}\nLanguage: ${language}\nCookies: ${cookieEnabled}\nStatus: ${onlineStatus}\nScreen: ${window.screen.width}x${window.screen.height}\nViewport: ${window.innerWidth}x${window.innerHeight}`;
  }

  const handleInputChange = (field: keyof BugReport, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Bug title is required';
    }
    if (!formData.summary.trim()) {
      newErrors.summary = 'Bug summary is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setSubmitSuccess(true);
      
      // Reset form after 2 seconds and close
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      alert('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        title: '',
        summary: '',
        browserInfo: getBrowserInfo(),
        userEmail: userEmail || ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (submitSuccess) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ bgcolor: 'success.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <BugReportIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Bug Report Submitted!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Thank you for helping us improve. We'll review your report and get back to you soon.
          </Typography>
          <LinearProgress sx={{ mt: 2, mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Closing automatically...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'warning.main' }}>
              <BugReportIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Report a Bug
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Help us improve by reporting issues you encounter
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Bug Title"
              placeholder="Brief description of the issue"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title || 'Provide a short, clear title for the bug'}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Bug Summary"
              placeholder="Describe what happened, what you expected, and any other relevant details..."
              value={formData.summary}
              onChange={(e) => handleInputChange('summary', e.target.value)}
              error={!!errors.summary}
              helperText={errors.summary || 'Provide a detailed description of the issue you encountered'}
              required
            />
          </Stack>

          {userEmail && (
            <Alert severity="info">
              We'll send updates about this bug report to: <strong>{userEmail}</strong>
            </Alert>
          )}

          {/* System Information (collapsed/minimized) */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              System information will be automatically included to help with debugging
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          startIcon={isSubmitting ? undefined : <SendIcon />}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
        </Button>
      </DialogActions>

      {isSubmitting && <LinearProgress />}
    </Dialog>
  );
};

export default BugReportModal;